import { useState, useEffect, useCallback, useRef } from 'react'
import { getProductSearchSuggestionsHelper, getProductDetailByIdHelper } from '../../helper/personal-details-helper'
import { useDebounce } from './use-debounce';

export function useProductSearch(updateProductDetails, handleProductUpdate) {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [selectedSearchTerm, setSelectedSearchTerm] = useState(undefined)
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const abortControllerRef = useRef(null)

  const clearSearchData = () => {
    setSearchSuggestions([])
    setSelectedSearchTerm(undefined)
  }

  const handleSearchSuggestions = useCallback(async (term) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    if (!term?.length) {
      clearSearchData()
      return
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    getProductSearchSuggestionsHelper(term, abortController.signal)
      .then((results) => {
        if (abortController.signal.aborted) return
        setSearchSuggestions(results)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    handleSearchSuggestions(debouncedSearchTerm)
  }, [debouncedSearchTerm, handleSearchSuggestions])

  useEffect(() => {
    const fetchProductDetails = async (id) => {
      try {
        const data = await getProductDetailByIdHelper(id)
        updateProductDetails(data)

        // Auto-fill first batch
        if (data?.inventory?.length > 0) {
          const firstBatch = [...data.inventory].sort(
            (a, b) =>
              new Date(a.expiry_date) - new Date(b.expiry_date)
          )[0]

          handleProductUpdate(
            data.product_id,
            'product_batch_inventory_id',
            firstBatch.product_batch_inventory_id
          )
          handleProductUpdate(
            data.product_id,
            'selected_batch_details',
            firstBatch
          )
        }
      } catch (error) {
        console.error('Error fetching product details:', error)
      }
    }

    if (selectedSearchTerm) fetchProductDetails(selectedSearchTerm)
  }, [selectedSearchTerm, updateProductDetails, handleProductUpdate])

  return {
    searchTerm,
    setSearchTerm,
    searchSuggestions,
    selectedSearchTerm,
    setSelectedSearchTerm,
    clearSearchData,
  }
}
